import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { usePagedListUrlState } from '../../../lib/query/usePagedListUrlState';
import { useUrlListFilter, serializeValue } from '../../../lib/query/useUrlListFilter';
import { CAMPAIGN_FILTER_FIELDS } from '../../../routes/filterMaps';
import { Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';
import CampaignFilterBar from './CampaignFilterBar';
import CampaignFormDrawer from './CampaignFormDrawer';
import CampaignViewDrawer from './CampaignViewDrawer';
import { useCampaigns } from '../hooks/useCampaigns';
import { useDeleteCampaign } from '../hooks/useDeleteCampaign';
import type { CampaignFilterValues, CampaignListItem } from '../types';
import { CAMPAIGN_STATUS_LABELS } from '../constants';

const defaultFilter: CampaignFilterValues = {
  status: undefined,
  channelId: undefined,
};

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '-';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return '-';
  }
}

const CampaignsPage: React.FC = () => {
  const { page, pageSize, setPage, setPageSize } = usePagedListUrlState(10);
  const [filter] = useUrlListFilter(defaultFilter, CAMPAIGN_FILTER_FIELDS);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [editCampaignId, setEditCampaignId] = useState<string | null>(null);
  const [viewCampaignId, setViewCampaignId] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      status: filter.status,
      channelId: filter.channelId,
    }),
    [page, pageSize, filter.status, filter.channelId],
  );

  const { data: paged, isLoading } = useCampaigns(listParams);
  const loadingCampaigns = isInitialQueryLoading(isLoading, paged);
  const deleteMutation = useDeleteCampaign();

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    } else {
      setPage(newPage);
    }
  }, [pageSize, setPage, setPageSize]);

  const [, rawSetSearchParams] = useSearchParams();

  const handleFilterChange = useCallback((next: CampaignFilterValues) => {
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of CAMPAIGN_FILTER_FIELDS) {
          const val = (next as Record<string, unknown>)[def.field];
          const serialized = serializeValue(val, def);
          if (serialized !== undefined) {
            nextParams.set(def.param, serialized);
          } else {
            nextParams.delete(def.param);
          }
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const handleFilterReset = useCallback(() => {
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of CAMPAIGN_FILTER_FIELDS) {
          nextParams.delete(def.param);
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const handleAdd = () => {
    setEditCampaignId(null);
    setFormDrawerOpen(true);
  };

  const handleEdit = useCallback((id: string) => {
    setEditCampaignId(id);
    setFormDrawerOpen(true);
  }, []);

  const columns: ColumnsType<CampaignListItem> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
      },
      {
        title: 'Channel',
        dataIndex: 'channelLabel',
        key: 'channelLabel',
        render: (v: string | null) => v ?? '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (v: number) => CAMPAIGN_STATUS_LABELS[v as keyof typeof CAMPAIGN_STATUS_LABELS] ?? '-',
      },
      {
        title: 'Start',
        dataIndex: 'startDateUtc',
        key: 'startDateUtc',
        render: (v: string | null) => formatDate(v),
      },
      {
        title: 'End',
        dataIndex: 'endDateUtc',
        key: 'endDateUtc',
        render: (v: string | null) => formatDate(v),
      },
      {
        title: 'Budget',
        dataIndex: 'budget',
        key: 'budget',
        render: (v: number | null) => (v != null ? v.toLocaleString() : '-'),
      },
      {
        title: 'Goal',
        dataIndex: 'targetedAmountGoal',
        key: 'targetedAmountGoal',
        render: (v: number | null) => (v != null ? v.toLocaleString() : '-'),
      },
      buildCogActionsColumn<CampaignListItem>({
        onView: (record) => setViewCampaignId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        deleteConfirm: () => ({
          title: 'Delete campaign',
          content: 'Are you sure you want to delete this campaign?',
        }),
        rowNumber: (_record, index) => (page - 1) * pageSize + index + 1,
      }),
    ],
    [handleEdit, deleteMutation, page, pageSize],
  );

  return (
    <>
      <ListPage
        title="Campaigns"
        subtitle="Plan marketing campaigns, channels, budgets, and goals."
        primaryAction={{
          permission: 'campaigns.create',
          label: 'Add campaign',
          onClick: handleAdd,
        }}
        filters={(
          <CampaignFilterBar
            value={filter}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
          />
        )}
      >
        <DataTable<CampaignListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingCampaigns}
          rowKey="id"
          bordered
          striped
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No campaigns yet. Add your first campaign."
        />
      </ListPage>

      <CampaignFormDrawer
        open={formDrawerOpen}
        onClose={() => {
          setFormDrawerOpen(false);
          setEditCampaignId(null);
        }}
        onSuccess={() => setFormDrawerOpen(false)}
        editCampaignId={editCampaignId}
      />

      <CampaignViewDrawer
        open={viewCampaignId != null}
        onClose={() => setViewCampaignId(null)}
        campaignId={viewCampaignId}
        onEdit={(id) => {
          setViewCampaignId(null);
          setEditCampaignId(id);
          setFormDrawerOpen(true);
        }}
      />
    </>
  );
};

export default CampaignsPage;
